import { useState } from "react";
import { useApi, mutar } from "../../lib/useApi";
import { Card, Button, Input, SectionLabel, EmptyState, useToast, cn } from "../../components/ui";
import type { SesionActiva } from "../../lib/tipos";
import { fechaCorta } from "../../lib/fecha-ui";

// Dispositivos conectados / grabadores del A10 (Ajustes › detalle, B10.1 §8). Restyle a tema claro;
// MISMO comportamiento: el admin crea un grabador y recibe su token EN CLARO una sola vez → lo pega
// en la grabadora del teléfono (`#/grabadora`). Kill-switch por grabador. El estado en vivo (en
// línea/batería) NO tiene telemetría en el modelo: solo se muestra encendido/apagado (el kill-switch).

type Dispositivo = { id: string; nombre: string; activo: number; sucursal_id: string; created_at: string };
type Sucursal = { id: string; nombre: string };


export function Grabadores({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  const [sucSel, setSucSel] = useState<string>("");
  const sucursales = useApi<{ sucursales: Sucursal[] }>(esSuper ? "/sucursales" : null);
  const suc = esSuper ? sucSel : (sesion.usuario.sucursalId ?? "");
  const q = esSuper && suc ? `?sucursal_id=${suc}` : "";
  const lista = useApi<{ dispositivos: Dispositivo[] }>(!esSuper || suc ? `/dispositivos${q}` : null, [suc]);
  // «Última grabación» (C-12, tramo 07): SOLO la fecha de cada grabador — nada de texto ni de audio.
  const ultimas = useApi<{ grabadores: { dispositivo_id: string; ultima_grabacion_at: string | null }[] }>(
    !esSuper || suc ? `/dispositivos/ultima-grabacion${q}` : null,
    [suc],
  );
  const ultimaDe = (id: string) => ultimas.data?.grabadores.find((g) => g.dispositivo_id === id)?.ultima_grabacion_at ?? null;
  const toast = useToast();

  const [creando, setCreando] = useState(false);
  const [tokenNuevo, setTokenNuevo] = useState<{ nombre: string; token: string } | null>(null);

  const items = lista.data?.dispositivos ?? [];
  const selectCls = "w-full rounded-[9px] border border-line-input bg-field px-3 py-2.5 text-[13px] text-ink outline-none";

  return (
    <div className="flex max-w-[820px] flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <SectionLabel>Oído del mostrador</SectionLabel>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-2">La llave que pide el instalador del oído en la compu de la botica. Con ella la compu sube solo los faltantes y las ventas oídas.</p>
        </div>
        {(!esSuper || suc) && (
          <Button variant={creando ? "outline" : "primary"} size="sm" onClick={() => { setCreando((v) => !v); setTokenNuevo(null); }}>
            {creando ? "Cerrar" : "Nueva llave del oído"}
          </Button>
        )}
      </div>

      {esSuper && (
        <select value={sucSel} onChange={(e) => setSucSel(e.target.value)} className={selectCls}>
          <option value="">— Elige una botica —</option>
          {(sucursales.data?.sucursales ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.nombre}</option>
          ))}
        </select>
      )}

      {tokenNuevo && <TokenNuevo dato={tokenNuevo} onCerrar={() => setTokenNuevo(null)} onToast={toast} />}

      {creando && (!esSuper || suc) && (
        <CrearGrabador
          sucursalQuery={q}
          onCreado={(d) => {
            setCreando(false);
            setTokenNuevo({ nombre: d.nombre, token: d.token });
            lista.recargar();
          }}
        />
      )}

      {esSuper && !suc ? (
        <Card><EmptyState title="Elige una botica" subtitle="Selecciona arriba para ver sus grabadores." /></Card>
      ) : (
        <Card>
          {lista.cargando ? (
            <p className="py-4 text-center text-[13px] text-ink-3">Cargando grabadores…</p>
          ) : lista.error ? (
            <div className="py-4 text-center">
              <p className="text-[13px] text-accent-ink">{lista.error}</p>
              <button onClick={lista.recargar} className="mt-2 text-[12.5px] text-link underline">Reintentar</button>
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="Sin llave del oído" subtitle="Créala con “Nueva llave del oído” y pégala en el instalador." />
          ) : (
            <ul className="flex flex-col">
              {items.map((d) => (
                <FilaGrabador key={d.id} d={d} ultima={ultimas.data ? ultimaDe(d.id) : undefined} sucursalQuery={q} onCambio={lista.recargar} onToast={toast} />
              ))}
            </ul>
          )}
        </Card>
      )}

      <p className="text-[12px] leading-relaxed text-ink-3">
        Desde el 17-oct-2026 el audio ya no sube a la nube: la llave de un grabador la usa la <b>compu de la botica</b> (el instalador del oído la pide). El celular abre la página de la compu por Tailscale, no esta web.
      </p>

      {(!esSuper || suc) && <PuenteCamaras sucursalQuery={q} onToast={toast} {...LLAVE_PUENTE} />}
      {(!esSuper || suc) && <PuenteCamaras sucursalQuery={q} onToast={toast} {...LLAVE_WEBCAM} />}
    </div>
  );
}

// Ojo v1 (11-oct-2026): el puente de cámaras de la botica (el programa que corre en la compu de la
// botica junto a Frigate) usa su PROPIA llave. Esa llave solo sirve para mandar alertas de cámara:
// no sube audio ni entra al sistema. Mismo patrón que los grabadores: el token se ve una sola vez.
type Llave = { tipo: "camara_hikvision" | "webcam_mostrador"; titulo: string; nombreNuevo: string; descripcion: string; vacio: string; apagada: string; encendida: string };
const LLAVE_PUENTE: Llave = {
  tipo: "camara_hikvision",
  titulo: "Cámaras",
  nombreNuevo: "Puente de cámaras",
  descripcion: "La llave del programa que mira las cámaras en la compu de la botica. Solo sirve para mandar alertas a Casos.",
  vacio: "Sin puente de cámaras",
  apagada: "Puente de cámaras apagado: ya no llegan alertas.",
  encendida: "Puente de cámaras encendido.",
};
// Clientes por cara (tramo 10): la webcam del mostrador manda solo huellas (números), nunca fotos. Su llave no
// sirve para Casos ni para el audio; la del puente de cámaras no sirve para reconocer clientes.
const LLAVE_WEBCAM: Llave = {
  tipo: "webcam_mostrador",
  titulo: "Webcam del mostrador",
  nombreNuevo: "Webcam del mostrador",
  descripcion: "La llave del programa que reconoce a los clientes registrados con su permiso. Manda solo huellas (números): ninguna foto sale de la compu.",
  vacio: "Sin webcam del mostrador",
  apagada: "Webcam apagada: ya no se reconoce a nadie.",
  encendida: "Webcam encendida.",
};

function PuenteCamaras({ sucursalQuery, onToast, tipo, titulo, nombreNuevo, descripcion, vacio, apagada, encendida }: { sucursalQuery: string; onToast: (m: string) => void } & Llave) {
  const sep = sucursalQuery ? "&" : "?";
  const lista = useApi<{ dispositivos: Dispositivo[] }>(`/dispositivos${sucursalQuery}${sep}tipo=${tipo}`, [sucursalQuery]);
  const [tokenNuevo, setTokenNuevo] = useState<{ nombre: string; token: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const items = lista.data?.dispositivos ?? [];

  async function crear() {
    setOcupado(true);
    try {
      const d = await mutar<{ id: string; nombre: string; token: string }>(`/dispositivos${sucursalQuery}`, { method: "POST", body: { tipo, nombre: nombreNuevo } });
      setTokenNuevo({ nombre: d.nombre, token: d.token });
      lista.recargar();
    } catch (e) {
      onToast(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alternar(d: Dispositivo) {
    setOcupado(true);
    try {
      await mutar(`/dispositivos/${d.id}${sucursalQuery}`, { method: "PATCH", body: { activo: d.activo !== 1, tipo } });
      onToast(d.activo === 1 ? apagada : encendida);
      lista.recargar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <SectionLabel>{titulo}</SectionLabel>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-2">{descripcion}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void crear()} disabled={ocupado}>Nueva llave</Button>
      </div>
      {tokenNuevo && <TokenNuevo dato={tokenNuevo} onCerrar={() => setTokenNuevo(null)} onToast={onToast} />}
      <Card>
        {lista.cargando ? (
          <p className="py-4 text-center text-[13px] text-ink-3">Cargando…</p>
        ) : items.length === 0 ? (
          <EmptyState title={vacio} subtitle="Crea una llave cuando se instale el programa en la compu de la botica." />
        ) : (
          <ul className="flex flex-col">
            {items.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 border-b border-line-row py-3 last:border-0">
                <div className="min-w-0">
                  <p className={cn("truncate text-[13px] font-semibold", d.activo !== 1 ? "text-ink-3 line-through" : "text-ink")}>{d.nombre}</p>
                  <p className="truncate text-[12px] text-ink-2">{d.activo === 1 ? "encendido" : "apagado"} · desde {d.created_at.slice(0, 10)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {d.activo !== 1 && <BotonEliminar d={d} query={`${sucursalQuery}${sep}tipo=${tipo}`} onListo={() => lista.recargar()} onToast={onToast} />}
                  <button
                    onClick={() => void alternar(d)}
                    disabled={ocupado}
                    className={cn(
                      "shrink-0 rounded-[8px] px-2.5 py-1 text-[12px] font-semibold transition-colors disabled:opacity-50 max-md:min-h-[44px]",
                      d.activo === 1 ? "bg-accent-soft text-accent-ink hover:bg-accent-soft-2" : "bg-ok-soft text-ok hover:opacity-90",
                    )}
                  >
                    {d.activo === 1 ? "Apagar" : "Encender"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function TokenNuevo({ dato, onCerrar, onToast }: { dato: { nombre: string; token: string }; onCerrar: () => void; onToast: (m: string) => void }) {
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(dato.token);
      setCopiado(true);
      onToast("Token copiado.");
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* sin permiso de portapapeles: el usuario copia a mano */
    }
  }
  return (
    <Card className="gap-2 border-tip-border bg-ok-soft">
      <p className="text-[13px] font-semibold text-ok">Token de “{dato.nombre}” — cópialo AHORA</p>
      <p className="text-[12px] text-ink-2">Solo se muestra esta vez. Pégalo en el equipo que lo va a usar. Si lo pierdes, crea otro.</p>
      <div className="break-all rounded-[8px] border border-line-inset bg-inset p-2 font-mono text-[12px] text-ink-strong">{dato.token}</div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void copiar()}>{copiado ? "✓ Copiado" : "Copiar token"}</Button>
        <Button variant="outline" size="sm" onClick={onCerrar}>Listo</Button>
      </div>
    </Card>
  );
}

function CrearGrabador({ sucursalQuery, onCreado }: { sucursalQuery: string; onCreado: (d: { id: string; nombre: string; token: string }) => void }) {
  const [nombre, setNombre] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function crear() {
    setEnviando(true);
    setError(null);
    try {
      const d = await mutar<{ id: string; nombre: string; token: string }>(`/dispositivos${sucursalQuery}`, { method: "POST", body: { nombre: nombre.trim() || "Oído compu" } });
      onCreado(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Card className="gap-3">
      <SectionLabel>Nueva llave del oído</SectionLabel>
      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre (ej. Oído compu)" />
      {error && <p className="text-[12.5px] text-accent-ink">{error}</p>}
      <div>
        <Button onClick={() => void crear()} disabled={enviando}>
          {enviando ? "Creando…" : "Crear llave"}
        </Button>
      </div>
    </Card>
  );
}

// `ultima`: undefined = todavía cargando; null = nunca grabó; texto = fecha de su última grabación.
function FilaGrabador({ d, ultima, sucursalQuery, onCambio, onToast }: { d: Dispositivo; ultima: string | null | undefined; sucursalQuery: string; onCambio: () => void; onToast: (m: string) => void }) {
  const [ocupado, setOcupado] = useState(false);
  async function toggle() {
    setOcupado(true);
    try {
      await mutar(`/dispositivos/${d.id}${sucursalQuery}`, { method: "PATCH", body: { activo: d.activo !== 1 } });
      onToast(d.activo === 1 ? `${d.nombre} apagado.` : `${d.nombre} encendido.`);
      onCambio();
    } finally {
      setOcupado(false);
    }
  }
  return (
    <li className="flex items-center justify-between gap-3 border-b border-line-row py-3 last:border-0">
      <div className="min-w-0">
        <p className={cn("truncate text-[13px] font-semibold", d.activo !== 1 ? "text-ink-3 line-through" : "text-ink")}>{d.nombre}</p>
        <p className="truncate text-[12px] text-ink-2">{d.activo === 1 ? "encendido" : "apagado"} · desde {d.created_at.slice(0, 10)}</p>
        {ultima !== undefined && (
          <p className="truncate text-[12px] text-ink-2">Última grabación: {ultima ? fechaCorta(ultima) : "todavía ninguna"}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {d.activo !== 1 && <BotonEliminar d={d} query={sucursalQuery} onListo={onCambio} onToast={onToast} />}
        <button
          onClick={() => void toggle()}
          disabled={ocupado}
          className={cn(
            "shrink-0 rounded-[8px] px-2.5 py-1 text-[12px] font-semibold transition-colors disabled:opacity-50 max-md:min-h-[44px]",
            d.activo === 1 ? "bg-accent-soft text-accent-ink hover:bg-accent-soft-2" : "bg-ok-soft text-ok hover:opacity-90",
          )}
        >
          {d.activo === 1 ? "Apagar" : "Encender"}
        </button>
      </div>
    </li>
  );
}

// «Eliminar» de una llave APAGADA (Kevin, 19-oct-2026: las apagadas se acumulaban sin forma de quitarlas). Pide
// confirmación en la misma fila; la llave sale de la lista y deja de abrir el sistema (borrado lógico en la API).
export function BotonEliminar({ d, query, onListo, onToast }: { d: Dispositivo; query: string; onListo: () => void; onToast: (m: string) => void }) {
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  async function eliminar() {
    setOcupado(true);
    try {
      await mutar(`/dispositivos/${d.id}${query}`, { method: "DELETE" });
      onToast(`${d.nombre} eliminada.`);
      onListo();
    } catch (e) {
      onToast(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
      setConfirmando(false);
    }
  }
  if (!confirmando) {
    return (
      <button
        onClick={() => setConfirmando(true)}
        className="shrink-0 rounded-[8px] px-2.5 py-1 text-[12px] font-semibold text-ink-2 transition-colors hover:text-accent-ink max-md:min-h-[44px]"
      >
        Eliminar
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <button
        onClick={() => void eliminar()}
        disabled={ocupado}
        className="shrink-0 rounded-[8px] bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent-ink disabled:opacity-50 max-md:min-h-[44px]"
      >
        {ocupado ? "Eliminando…" : "Sí, eliminar"}
      </button>
      <button onClick={() => setConfirmando(false)} className="shrink-0 px-2 py-1 text-[12px] text-ink-2 max-md:min-h-[44px]">
        No
      </button>
    </span>
  );
}
